import React, { createContext, useContext, ReactNode, useMemo } from "react";
import { Issue } from "generated-graphql/graphql";
import { DueDateWarningsByUserId } from "hooks/useSubmissionForm";

interface SubmissionFormContextType {
  issues: Issue[];
  dueDateWarningsByUserId: DueDateWarningsByUserId;
}

const SubmissionFormContext = createContext<
  SubmissionFormContextType | undefined
>(undefined);

interface SubmissionFormProviderProps {
  children: ReactNode;
  issues: Issue[];
  dueDateWarningsByUserId?: DueDateWarningsByUserId;
}

export function SubmissionFormProvider({
  children,
  issues,
  dueDateWarningsByUserId = {},
}: SubmissionFormProviderProps) {
  return (
    <SubmissionFormContext.Provider value={{ issues, dueDateWarningsByUserId }}>
      {children}
    </SubmissionFormContext.Provider>
  );
}

export function useSubmissionFormContext() {
  const context = useContext(SubmissionFormContext);
  if (context === undefined) {
    throw new Error(
      "useSubmissionFormContext must be used within a SubmissionFormProvider"
    );
  }
  return context;
}

export function useReviewersIssues() {
  const { issues } = useSubmissionFormContext();
  return useMemo(
    () =>
      issues.filter(
        (i) => i.modelName === "Submission" && i.key === "reviewers"
      ),
    [issues]
  );
}

export function useReviewerIssues() {
  const { issues } = useSubmissionFormContext();
  return useMemo(
    () => issues.filter((i) => i.modelName === "SubmissionReviewer"),
    [issues]
  );
}

export function useDueDateWarningsByUserId(): DueDateWarningsByUserId {
  const { dueDateWarningsByUserId } = useSubmissionFormContext();
  return dueDateWarningsByUserId;
}
